// Tenantra Inc. - Professional Ecosystem & Roadmap Page
import { useState } from 'react';
import { Logo } from '@/components/Logo';
import { 
  ArrowLeft, Building2, ExternalLink, CheckCircle2, Circle,
  Play, MessageCircle
} from 'lucide-react';

// Tokenomics Data
const TOKENOMICS = [
  { label: 'Community & Rewards', percent: 40, amount: '400M', color: '#14B8A6' },
  { label: 'Ecosystem Development', percent: 20, amount: '200M', color: '#6366F1' },
  { label: 'Team & Advisors', percent: 15, amount: '150M', color: '#A855F7' },
  { label: 'Liquidity', percent: 15, amount: '150M', color: '#F59E0B' },
  { label: 'Treasury', percent: 10, amount: '100M', color: '#64748B' },
];

// Pie Chart Component
function TokenPieChart() {
  let cumulativePercent = 0;
  
  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <div className="relative w-36 h-36 sm:w-44 sm:h-44 mx-auto">
      <svg viewBox="-1 -1 2 2" className="transform -rotate-90 w-full h-full drop-shadow-lg">
        {TOKENOMICS.map((item, i) => {
          const [startX, startY] = getCoordinatesForPercent(cumulativePercent / 100);
          cumulativePercent += item.percent;
          const [endX, endY] = getCoordinatesForPercent(cumulativePercent / 100);
          const largeArcFlag = item.percent > 50 ? 1 : 0;
          const pathData = [
            `M ${startX} ${startY}`,
            `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
            `L 0 0`,
          ].join(' ');
          
          return (
            <path
              key={i}
              d={pathData}
              fill={item.color}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="0.02"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <p className="text-base sm:text-lg font-bold text-white">1B</p>
            <p className="text-[8px] sm:text-[9px] text-slate-400">SUPPLY</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EcosystemPage() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <div className="flex items-center gap-4">
              <a href="/whitepaper" className="text-sm text-gray-600 hover:text-teal-600">
                Whitepaper
              </a>
              <a href="/" className="text-sm text-gray-500 hover:text-teal-600 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Home</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full blur-[100px]"></div>
        </div>
        
        <div className="relative z-10 px-4 py-12 sm:py-16 text-center max-w-3xl mx-auto">
          <p className="text-teal-400 text-xs sm:text-sm font-medium tracking-widest mb-3">TENANTRA ECOSYSTEM</p>
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Community & Roadmap
          </h1>
          
          <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-xl mx-auto">
            Join the Tenantra community, earn rewards through our Aviator game, and be part of Canada's rental revolution.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a 
              href="https://t.me/teaborabora_bot/tentgame" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-white font-medium rounded-lg transition-colors text-sm"
            >
              <Play className="w-4 h-4 fill-current" />
              Play Aviator Game
            </a>
            <a 
              href="https://t.me/tenantra" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors text-sm border border-white/20"
            >
              <MessageCircle className="w-4 h-4" />
              Join Telegram
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 sticky top-[57px] bg-white z-40">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'game', label: 'Aviator Game' },
              { id: 'tiers', label: 'Reward Tiers' },
              { id: 'tokenomics', label: 'Tokenomics' },
              { id: 'roadmap', label: 'Roadmap' },
              { id: 'faq', label: 'FAQ' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-teal-500 text-teal-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Welcome to the Tenantra Ecosystem</h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              The Tenantra Ecosystem combines gaming, community building, and token rewards to create an engaged network of early supporters. Our goal is to build the strongest community in Canadian PropTech before launching the $TNTRA token.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">How It Works</h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                <div>
                  <p className="font-medium text-gray-900">Play the Aviator Game</p>
                  <p className="text-sm text-gray-600">Earn TN Points by playing our Telegram-based crash game. Start with 1,000 free points.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                <div>
                  <p className="font-medium text-gray-900">Climb the Tiers</p>
                  <p className="text-sm text-gray-600">Accumulate points to unlock higher tiers with better rewards and exclusive benefits.</p>
                </div>
              </div>
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                <div>
                  <p className="font-medium text-gray-900">Earn Token Allocation</p>
                  <p className="text-sm text-gray-600">Your TN Points convert to $TNTRA token allocation when we launch in Q3 2026.</p>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Why Join Early?</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>The "Early Supporter" tier is time-limited and will be locked forever after Phase 1</li>
              <li>Early players get better conversion rates for $TNTRA tokens</li>
              <li>Exclusive NFTs only available during the early community phase</li>
              <li>Direct influence on platform development through governance</li>
            </ul>
          </div>
        )}

        {/* Aviator Game Tab */}
        {activeTab === 'game' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Tenantra Aviator Game</h2>
            
            {/* Game Card - Highlighted */}
            <div className="relative overflow-hidden rounded-2xl mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-slate-900 to-slate-800"></div>
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500 rounded-full blur-[80px]"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500 rounded-full blur-[60px]"></div>
              </div>
              
              <div className="relative z-10 p-5 sm:p-8">
                <p className="text-purple-300 text-sm mb-2">Telegram Mini App</p>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Crash-Style Game</h3>
                
                <p className="text-slate-300 text-sm sm:text-base mb-6">
                  Bet your TN Points and watch the multiplier rise. Cash out before it crashes to multiply your points. The higher you go, the bigger the reward, but wait too long and you lose everything.
                </p>
                
                <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                    <p className="text-xl sm:text-2xl font-bold text-white">1,000</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Free Points</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                    <p className="text-xl sm:text-2xl font-bold text-white">5</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Reward Tiers</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                    <p className="text-xl sm:text-2xl font-bold text-white">∞</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Potential</p>
                  </div>
                </div>

                <a 
                  href="https://t.me/teaborabora_bot/tentgame" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-white font-medium rounded-lg transition-colors text-sm"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Play Now
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Play</h3>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2 mb-8">
              <li>Open the game in Telegram and claim your 1,000 free TN Points</li>
              <li>Place your bet before the round starts</li>
              <li>Watch the multiplier increase from 1.00x upwards</li>
              <li>Click "Cash Out" to lock in your multiplied points</li>
              <li>If the plane crashes before you cash out, you lose your bet</li>
            </ol>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ways to Earn More Points</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Referrals:</strong> Invite friends and earn bonus points when they join</li>
              <li><strong>Daily Bonuses:</strong> Log in daily for streak rewards</li>
              <li><strong>Tournaments:</strong> Compete in weekly competitions for prize pools</li>
              <li><strong>Achievements:</strong> Complete milestones for bonus rewards</li>
            </ul>
          </div>
        )}

        {/* Reward Tiers Tab */}
        {activeTab === 'tiers' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Reward Tiers</h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              As you accumulate TN Points, you unlock higher tiers with increasingly valuable rewards. Each tier provides cumulative benefits, meaning you keep all rewards from previous tiers.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 font-semibold text-gray-900">Tier</th>
                    <th className="text-left py-3 font-semibold text-gray-900">Points Required</th>
                    <th className="text-left py-3 font-semibold text-gray-900">Key Benefits</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-100">
                    <td className="py-3 font-medium">Early Supporter</td>
                    <td className="py-3">5,000</td>
                    <td className="py-3">NFT badge, whitelist access, permanent early badge</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 font-medium">Network Builder</td>
                    <td className="py-3">25,000</td>
                    <td className="py-3">Guaranteed $TNTRA allocation, governance access</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 font-medium">Network Insider</td>
                    <td className="py-3">75,000</td>
                    <td className="py-3">Increased allocation, first access to new features</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 font-medium">Network Leader</td>
                    <td className="py-3">200,000</td>
                    <td className="py-3">Maximum allocation, direct decision influence</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 font-medium">Genesis Circle</td>
                    <td className="py-3">500,000</td>
                    <td className="py-3">Strategic partner status, lifetime recognition</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="font-medium text-amber-800 mb-2">Time-Limited Opportunity</p>
              <p className="text-sm text-amber-700">
                The "Early Supporter" tier and its exclusive NFT are only available during Phase 0 and Phase 1. Once Phase 2 begins, this tier will be permanently locked and can never be earned again.
              </p>
            </div>
          </div>
        )}

        {/* Tokenomics Tab */}
        {activeTab === 'tokenomics' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">$TNTRA Tokenomics</h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              The $TNTRA token is the native utility token of the Tenantra ecosystem. It will be used for platform payments, staking rewards, governance voting, and more.
            </p>

            {/* Token Overview Card - Highlighted */}
            <div className="relative overflow-hidden rounded-2xl mb-8">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900"></div>
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500 rounded-full blur-[100px]"></div>
              </div>
              
              <div className="relative z-10 p-5 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-white/10">
                  <div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white">$TNTRA</h3>
                    <p className="text-slate-400 text-sm">Tenantra Token</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-white">1,000,000,000</p>
                    <p className="text-slate-400 text-sm">Total Supply (Fixed)</p>
                  </div>
                </div>
                
                <div className="flex flex-col lg:flex-row gap-6 mb-6">
                  <div className="flex-shrink-0">
                    <TokenPieChart />
                  </div>
                  
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white mb-3">Allocation</p>
                    <div className="space-y-2">
                      {TOKENOMICS.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-slate-300 flex-1">{item.label}</span>
                          <span className="text-sm text-white font-medium">{item.percent}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#0098EA] flex items-center justify-center">
                      <span className="text-white font-bold text-xs">TON</span>
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">The Open Network</p>
                      <p className="text-slate-400 text-xs">Powered by Telegram</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Contract Address</p>
                  <p className="text-xs font-mono text-slate-300 break-all mb-2">
                    EQC7IUDp5S5jZ0LuX5Vd9xc0Wmq5scusjZZeW4MzCkwTZQPi
                  </p>
                  <a 
                    href="https://tonviewer.com/EQC7IUDp5S5jZ0LuX5Vd9xc0Wmq5scusjZZeW4MzCkwTZQPi" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#0098EA] hover:text-[#00b4ff]"
                  >
                    View on TON Explorer
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-4">Token Utility</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-8">
              <li><strong>Platform Payments:</strong> Pay for Tenantra subscriptions with 20% discount</li>
              <li><strong>Staking:</strong> Stake tokens to earn passive rewards</li>
              <li><strong>Governance:</strong> Vote on platform decisions and feature priorities</li>
              <li><strong>Game Rewards:</strong> TN Points convert to $TNTRA at token launch</li>
              <li><strong>NFT Minting:</strong> Use $TNTRA to mint and trade tier NFTs</li>
            </ul>

            <p className="text-sm text-gray-500">
              For complete tokenomics details including vesting schedules, see our <a href="/whitepaper" className="text-teal-600 hover:underline">Whitepaper</a>.
            </p>
          </div>
        )}

        {/* Roadmap Tab */}
        {activeTab === 'roadmap' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Project Roadmap</h2>
            
            <p className="text-gray-700 leading-relaxed mb-8">
              Our development roadmap outlines the key milestones from community building through token launch and beyond. We are committed to transparency and will update the community on progress regularly.
            </p>

            <div className="space-y-8">
              {/* Phase 0 */}
              <div className="border-l-4 border-emerald-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded">COMPLETED</span>
                  <span className="text-sm text-gray-500">Q4 2025</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 0: Foundation</h3>
                <ul className="space-y-2">
                  {['Aviator Game Launch', 'TN Points System', 'Community Discord & Telegram', '1,000 Free Points for Early Adopters', 'Tier Reward Structure', 'Referral Program'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 1 */}
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">IN PROGRESS</span>
                  <span className="text-sm text-gray-500">Q1 2026</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 1: Community Growth</h3>
                <ul className="space-y-2">
                  {[
                    { text: 'Ambassador Program Launch', done: true },
                    { text: '10,000 Active Players', done: false },
                    { text: 'Weekly Tournaments', done: false },
                    { text: 'Leaderboard Seasons', done: false },
                    { text: 'Strategic Partnerships', done: false },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      {item.done ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 2 */}
              <div className="border-l-4 border-gray-300 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">UPCOMING</span>
                  <span className="text-sm text-gray-500">Q2 2026</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 2: NFT Collection</h3>
                <ul className="space-y-2">
                  {['Tier NFT Minting', 'NFT Staking for Bonus Points', 'Exclusive Game Modes for Holders', 'Secondary Marketplace'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 3 */}
              <div className="border-l-4 border-gray-300 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">UPCOMING</span>
                  <span className="text-sm text-gray-500">Q3 2026</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 3: Token Launch</h3>
                <ul className="space-y-2">
                  {['Smart Contract Audit', 'Private Sale (Genesis Circle)', 'Public Presale', 'DEX Listing', 'Liquidity Mining'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 4 */}
              <div className="border-l-4 border-gray-300 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">UPCOMING</span>
                  <span className="text-sm text-gray-500">Q4 2026</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 4: Expansion</h3>
                <ul className="space-y-2">
                  {['CEX Listings', 'DAO Governance', 'Staking & Yield Farming', 'Mobile App', 'Real Estate Integration'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Phase 5 */}
              <div className="border-l-4 border-gray-300 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">FUTURE</span>
                  <span className="text-sm text-gray-500">2027+</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Phase 5: Full Integration</h3>
                <ul className="space-y-2">
                  {['$TNTRA Platform Payments', 'Cross-Chain Expansion', 'Global Market Expansion', 'Landlord/Tenant Incentives'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What is Tenantra Aviator?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Tenantra Aviator is a crash-style game built on Telegram. You bet TN Points and watch a multiplier rise. Cash out before it crashes to multiply your points. It's free to start with 1,000 TN Points.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What are TN Points?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  TN Points are in-game currency that track your progress in the Tenantra ecosystem. They will be convertible to $TNTRA tokens at launch based on your tier and conversion rate.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">When does the $TNTRA token launch?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The token is scheduled to launch in Q3 2026 after smart contract audits and private/public sales. Genesis Circle members get first access.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How do I earn more TN Points?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  You can earn points by playing the Aviator game, referring friends, completing daily challenges, participating in tournaments, and achieving milestones.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">What blockchain is $TNTRA on?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  $TNTRA is deployed on The Open Network (TON), the blockchain integrated with Telegram. This allows seamless interaction between the Telegram game and token.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Is my data safe?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Yes. Tenantra is PIPEDA compliant and follows strict Canadian privacy laws. Your personal data is encrypted and never shared without consent.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">How is this connected to Tenantra's rental platform?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The Aviator game and $TNTRA token are part of Tenantra's Web3 ecosystem. Early supporters will receive exclusive benefits when the full rental verification platform launches, including discounts and priority access.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <div className="flex justify-center mb-4"><Logo size="md" /></div>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mb-4">
            <a href="/whitepaper" className="hover:text-teal-600">Whitepaper</a>
            <a href="/privacy-policy" className="hover:text-teal-600">Privacy Policy</a>
            <a href="/terms-of-service" className="hover:text-teal-600">Terms of Service</a>
            <a href="/" className="hover:text-teal-600">Main Website</a>
          </div>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Tenantra Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
